import {
    ContentFile,
    DocumentVersion,
    PublicationForm,
    PublicationGroupStatus,
    PublicationStatusEnum,
    PublicationType,
    StisPublicationAction,
} from '@web/vin-sessions/types';
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import colors from '@packages/core/styles/colors';
import { getLogger } from '@packages/core/logging';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { StisPublicationFormHeader } from './stis-publication-form-header';
import usePublicationForm from './hooks/use-publication-form';
import usePublicationFormDetails from './hooks/use-publication-form-detail.hooks';
import { DetailView, DetailViewBody, DetailViewContent } from '@web/components/detail-view';
import { PublicationFormRightSidebar } from './stis-publication-form-right-sidebar';
import { Typography } from '@packages/ui/shared';
import { DocumentInfo } from './document-info';
import { docsService, vinSessionService } from '@web/services/singletons';
import { AssociateSections } from './associate-sections';
import { useTranslation } from 'react-i18next';
import { DocumentSearchMetaData } from './document-search-metadata';
import { PreviewDescription } from './preview-description';
import { createUseStyles } from 'react-jss';
import { useAuthState } from '@packages/contexts/auth';
import { PublicationModal } from './publication-modal';
import { usePublicationsDispatch, usePublicationsState } from './context/publications.hooks';
import { publicationsActions } from './context/publications.state';
import { AssociateSectionModal } from './associate-sections-modal';
import { handleVinSessionError } from '@web/vin-sessions/utils/vinSessionUtils';
import type { VehicleOption } from './associate-vehicles-picker';
import { AssociateVehiclesModal } from './associate-vehicles-modal';
import { AssociatedVehiclesSummary } from './associated-vehicles-summary';
import { AssociatePublicationsCard } from './associate-publications-card';
import Loader from '@web/components/loader';
import { getNextVersion, parseVersion } from './version.utils';
import { getSaveErrorMessage } from './save-error.utils';
import { readDocumentUploadDetails, resolveUploadedContentFile } from './content-file.utils';
import { getPublicationsListPath } from './publication-list-filter.util';
import { isTechTipsLabel } from './vehicle-filter.utils';
import { CONTENT_FILE_PREVIEW_LABELS, downloadFromUrl, getContentFilePreview } from './content-file.utils';
import { PdfPreviewModal } from './pdf-preview-modal';
import { useCloseLeaveWhenSent, useFileSend } from './hooks/use-file-send';
import { OpenContentFileError, useOpenContentFile } from './hooks/use-open-content-file';
import { usePublishGuard } from './hooks/use-publish-guard';
import { AutosaveIndicator } from './autosave-indicator';
import { BLOCKED_REASON, SAVE_FAILED_REASON, getMissingRequiredFields, isLifecycleStatus } from './autosave.utils';
import { useLeaveGuard } from './hooks/use-leave-guard';
import {
    UPLOADING,
    isFailedUploadStatus,
    isUploadInProgress,
    readUploadStatus,
    useUploadStatus,
} from './hooks/use-upload-status';

const logger = getLogger('vin-sessions');

const useStyles = createUseStyles({
    scrollBar: {
        '&::-webkit-scrollbar': {
            width: 10,
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#ffffff',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.blueFour,
            borderRadius: 10,
            border: '2px solid #ffffff',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            cursor: 'pointer',
            backgroundColor: colors.blueOne,
        },
    },
});

export interface AssociatedVehiclesType {
    vehicles: VehicleOption[];
    allVehicles: boolean;
}

const getCurrentFormattedDate = () => {
    const now = new Date();
    return (
        now.toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
        }) +
        ' at ' +
        now.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })
    );
};

const StisPublicationForm: React.FC = () => {
    const { account, token } = useAuthState();
    const [filterResponse, setFilterResponse] = useState<any>();
    const [selectedSection, setSelectedSection] = useState<string>('');
    const publicationDispatch = usePublicationsDispatch();
    const navigate = useNavigate();
    const classes = useStyles();
    const { t } = useTranslation();
    const { publicationFormId } = useParams<{ publicationFormId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const publicationStatus = (searchParams.get('status') as PublicationStatusEnum) || PublicationStatusEnum.Draft;
    const [showDeletePublication, setShowDeletePublication] = useState<boolean>(false);
    const maxVersion = parseVersion(searchParams.get('maxVersion')) ?? 1;
    const action = searchParams.get('action') as StisPublicationAction;
    const { loading } = usePublicationsState();
    const [associatedVehicles, setAssociatedVehicles] = useState<AssociatedVehiclesType>();
    const [associatedPublications, setAssociatedPublications] = useState<any[]>([]);
    const [fileUploadStatus, setFileUploadStatus] = useState<boolean>(false);
    const [creatorUser, setCreatorUser] = useState<{
        firstName: string;
        lastName: string;
        name: string;
    } | null>(null);

    const [updatedUser, setUpdatedUser] = useState<{
        firstName: string;
        lastName: string;
        name: string;
    } | null>(null);

    const [parentPublications, setParentPublications] = useState<DocumentVersion[]>([]);
    const [vehicleOptions, setVehicleOptions] = useState<VehicleOption[]>([]);
    const [isLoadingVehicles, setIsLoadingVehicles] = useState<boolean>(false);
    const hasLoadedVehicleOptionsRef = useRef<boolean>(false);
    const [publicationTypes, setPublicationTypes] = useState<PublicationType[]>([]);
    const hasSavedRef = useRef(false);

    const upload = useUploadStatus();
    const isFileProcessing = isUploadInProgress(upload.phase);
    const canPublish = !isFileProcessing && upload.phase !== 'failed';

    const statusOfPublication = useMemo(
        () => ({
            isArchivedPublication: publicationStatus === PublicationStatusEnum.Archived,
            isPublishedPublication: publicationStatus === PublicationStatusEnum.Published,
            isDraftPublicaiton: publicationStatus === PublicationStatusEnum.Draft,
            isInReviewPublication: publicationStatus === PublicationStatusEnum.InReview,
        }),
        [publicationStatus]
    );

    useEffect(() => {
        const fetchPublicationTypes = async () => {
            try {
                const response = await docsService.getDocumentTypes({});
                if (response.success) {
                    setPublicationTypes(response.data || []);
                }
            } catch (error) {
                logger.error('Failed to fetch the publication types', { error });
            }
        };

        fetchPublicationTypes();
    }, []);

    // The header menu and the status options read `action` and `status` from the URL
    const handleAutosaved = useCallback(
        (saved: PublicationForm) => {
            hasSavedRef.current = true;
            const currentAction = searchParams.get('action');
            const currentStatus = searchParams.get('status');
            const nextAction = currentAction === StisPublicationAction.New ? StisPublicationAction.Edit : currentAction;
            const nextStatus = isLifecycleStatus(saved.status) ? saved.status : currentStatus;
            if (nextAction === currentAction && nextStatus === currentStatus) {
                return;
            }
            setSearchParams(
                (params) => {
                    const nextParams = new URLSearchParams(params);
                    if (nextAction) {
                        nextParams.set('action', nextAction);
                    }
                    if (nextStatus) {
                        nextParams.set('status', nextStatus);
                    }
                    return nextParams;
                },
                { replace: true }
            );
        },
        [searchParams, setSearchParams]
    );

    const {
        publicationInfo,
        setPublicationInfo,
        saveNow,
        saveAsDraft,
        getUnsavedChangesReason,
        savedStatus,
        autosave,
    } = usePublicationFormDetails({
        publicationFormId,
        publicationAction: action,
        maxVersion,
        publicationTypes,
        canPublish,
        onSaved: handleAutosaved,
        setCreatorUser,
        setUpdatedUser,
        setParentPublications,
    });

    // Only a publication that is not published yet waits for its file; a published one stays published
    const publishBlockedMessage =
        savedStatus === PublicationStatusEnum.Published
            ? null
            : isFileProcessing
              ? 'The file is still processing. Please check back later to publish.'
              : upload.phase === 'failed'
                ? 'The previous upload failed. Upload the file again to publish.'
                : null;

    const {
        state,
        actions: formActions,
        setState,
    } = usePublicationForm({
        setPublicationInformation: setPublicationInfo,
    });

    const {
        hasUnsavedChanges,
        markSaved,
        flush,
        status: autosaveStatus,
        errorMessage: autosaveErrorMessage,
    } = autosave;

    // The header only shows that a save failed, so the reason the backend gave is raised here
    useEffect(() => {
        if (autosaveStatus !== 'error') {
            return;
        }
        publicationDispatch(
            publicationsActions.setToastMessage({
                toast: {
                    toastMessage: autosaveErrorMessage || SAVE_FAILED_REASON,
                    iconColor: 'white',
                    iconName: 'x-close',
                },
            })
        );
    }, [autosaveStatus, autosaveErrorMessage, publicationDispatch]);

    useEffect(() => {
        return () => {
            if (
                action === StisPublicationAction.New &&
                publicationFormId &&
                !hasSavedRef.current &&
                !hasUnsavedChanges()
            ) {
                docsService.deletePublicationById(publicationFormId).catch(() => {});
            }
        };
    }, [action, publicationFormId, hasUnsavedChanges]);

    // A never-saved new publication is exempt, since it is deleted on leave anyway
    const hasChangesThatCannotSave = useCallback(
        () => !(action === StisPublicationAction.New && !hasSavedRef.current) && getUnsavedChangesReason() !== null,
        [action, getUnsavedChangesReason]
    );
    const fileSend = useFileSend(fileUploadStatus);
    const shouldHoldLeave = useCallback(
        () => fileUploadStatus || hasChangesThatCannotSave(),
        [fileUploadStatus, hasChangesThatCannotSave]
    );
    const leaveGuard = useLeaveGuard(shouldHoldLeave);
    const { leaveWith, leaveWithoutAsking } = leaveGuard;
    const [isSavingDraftToLeave, setIsSavingDraftToLeave] = useState(false);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (hasUnsavedChanges() || fileUploadStatus || hasChangesThatCannotSave()) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges, fileUploadStatus, hasChangesThatCannotSave]);

    const {
        start: startUploadTracking,
        resume: resumeUploadTracking,
        reset: resetUploadTracking,
        markFailed: markUploadFailed,
    } = upload;

    const getUploadStatusCheck = useCallback(
        (isZip: boolean) => () =>
            isZip
                ? docsService.uploadZipDocument({ documentId: publicationFormId || '' })
                : docsService.getUploadStatus({ body: { documentId: publicationFormId } }),
        [publicationFormId]
    );

    const findUploaderName = useCallback(async (accountId: string) => {
        const response = await docsService.getUserInfoCardsAccount(accountId);
        const account = response.success ? response.data?.account : undefined;
        return account ? `${account.firstName} ${account.lastName}` : undefined;
    }, []);
    // Saves the whole form once the file is in, which also puts back a status an older document lost
    // to the upload pipeline
    const handleUploadCompleted = useCallback(
        async (data: unknown, sentFile?: ContentFile) => {
            let source = data;
            if (!readDocumentUploadDetails(data) && !sentFile && publicationFormId) {
                try {
                    const response = await docsService.fetchPublicationFormById(publicationFormId);
                    if (response.success) {
                        source = response.data;
                    }
                } catch (error) {
                    logger.error('Failed to load the uploaded file details', { error, publicationFormId });
                }
            }
            const contentFile = await resolveUploadedContentFile(source, sentFile, findUploaderName);
            saveNow(contentFile ? { contentFile } : {});
        },
        [saveNow, findUploaderName, publicationFormId]
    );

    const handleFileSent = useCallback(
        ({ isZip, contentFile }: { isZip: boolean; contentFile: ContentFile }) => {
            fileSend.finish();
            startUploadTracking({
                check: getUploadStatusCheck(isZip),
                onUploaded: (data) => {
                    formActions.handleContentFileChange(contentFile);
                    formActions.handleFileUploadFlag(true);
                    void handleUploadCompleted(data, contentFile);
                    publicationDispatch(
                        publicationsActions.setToastMessage({
                            toast: {
                                toastMessage: 'File Uploaded Successfully',
                                iconColor: 'white',
                                iconName: 'checkmark',
                            },
                        })
                    );
                },
            });
        },
        [startUploadTracking, getUploadStatusCheck, formActions, publicationDispatch, handleUploadCompleted]
    );

    const trackedDocumentIdRef = useRef<string>();
    useEffect(() => {
        const documentId = publicationInfo.documentId;
        if (!documentId || trackedDocumentIdRef.current === documentId) {
            return;
        }
        trackedDocumentIdRef.current = documentId;
        const uploadStatus = readUploadStatus({
            uploadStatus: publicationInfo.uploadStatus,
            status: publicationInfo.status,
        });
        if (isFailedUploadStatus(uploadStatus)) {
            markUploadFailed();
            return;
        }
        if (uploadStatus !== UPLOADING) {
            resetUploadTracking();
            return;
        }
        const contentFile = publicationInfo.contentFile;
        const isZip = Boolean(
            !contentFile?.fileName ||
            contentFile.fileName.toLowerCase().endsWith('.zip') ||
            contentFile.fileType === 'ZIP'
        );
        resumeUploadTracking({
            check: getUploadStatusCheck(isZip),
            onUploaded: (data) => {
                void handleUploadCompleted(data);
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: 'The file finished processing',
                            iconColor: 'white',
                            iconName: 'checkmark',
                        },
                    })
                );
            },
        });
    }, [
        publicationInfo.documentId,
        publicationInfo.uploadStatus,
        publicationInfo.status,
        publicationInfo.contentFile,
        resetUploadTracking,
        resumeUploadTracking,
        markUploadFailed,
        getUploadStatusCheck,
        publicationDispatch,
        handleUploadCompleted,
    ]);

    const checkRequiredFields = useCallback(() => {
        formActions.handleTitleChange(publicationInfo.title);
        formActions.handlePublicationTypeChange(publicationInfo?.publicationType, publicationInfo?.contentFile);
        formActions.handleDocumentCodeChange(publicationInfo.docCode);
        formActions.handleVisibilityTypeChange(publicationInfo.visibilityLevel);
        formActions.handleContentURLChange(publicationInfo.url);
        formActions.handleContentFileChange(publicationInfo?.contentFile);
        formActions.handleDescriptionChange(publicationInfo.description);
        if (isTechTipsLabel(publicationInfo.publicationType?.label)) {
            formActions.handlePublishedYearChange(publicationInfo.publishedYear);
            formActions.handlePublishedMonthChange(publicationInfo.publishedMonth);
        }
    }, [publicationInfo, formActions]);

    const handleAssociatedPublicationsSaved = useCallback(
        (value: React.SetStateAction<any[]>) => {
            setAssociatedPublications(value);
            markSaved();
        },
        [markSaved]
    );

    const createNewVersion = useCallback(
        async (publicationFormId?: string, pubVersion?: number) => {
            // Finish saving this version before the form moves on to the new one
            await flush();
            const nextVersion = getNextVersion(pubVersion);
            const response = await docsService.createVersionDocumentId({
                pubVersion: nextVersion,
                parentDocumentId: publicationFormId || '',
            });
            if (response && !response.success) {
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: getSaveErrorMessage(response.data, 'The publication version was not created'),
                            iconColor: 'white',
                            iconName: 'x-close',
                        },
                    })
                );
            } else {
                if (typeof response.data === 'string') {
                    try {
                        const parsedData = JSON.parse(response.data);
                        if (parsedData && parsedData.documentId) {
                            navigate(
                                `/administration/publication/${parsedData.documentId}?action=${StisPublicationAction.AddVersion}&maxVersion=${nextVersion}&status=${PublicationStatusEnum.Draft}`
                            );
                        }
                    } catch (error) {
                        logger.error(
                            `Failed to parse the create version response data for the publication [${publicationFormId}]`,
                            { error, publicationFormId }
                        );
                    }
                }
            }
        },
        [flush, navigate, publicationDispatch]
    );

    const handleDuplicatePublication = useCallback(
        (publicationFormId?: string, pubVersion?: number) =>
            leaveWith(() => createNewVersion(publicationFormId, pubVersion)),
        [leaveWith, createNewVersion]
    );

    const handleDeletePublication = useCallback(
        async (publicationFormId?: string) => {
            setShowDeletePublication(false);
            if (!publicationFormId) {
                return;
            }
            // A save landing after the delete would bring the document back
            await flush();
            const response = await docsService.deletePublicationById(publicationFormId);
            if (!response.success) {
                if (!response.aborted) {
                    publicationDispatch(
                        publicationsActions.setToastMessage({
                            toast: {
                                toastMessage: getSaveErrorMessage(response.data, 'The publication was not deleted'),
                                iconColor: 'white',
                                iconName: 'x-close',
                            },
                        })
                    );
                }
                return;
            }
            publicationDispatch(
                publicationsActions.setToastMessage({
                    toast: {
                        toastMessage: 'The publication was successfully deleted',
                        iconColor: 'white',
                        iconName: 'checkmark',
                    },
                })
            );
            await leaveWithoutAsking(() => navigate(getPublicationsListPath(publicationStatus)));
        },
        [flush, navigate, publicationDispatch, publicationStatus, leaveWithoutAsking]
    );

    const getFilters = async () => {
        try {
            const response = await vinSessionService.getFilter({
                ignoreCache: true,
            });

            if (response.success && Object.keys(response.data).length > 0) {
                setFilterResponse(response.data);
            }
        } catch (error) {
            handleVinSessionError('Publications Fetch Error', error);
        }
    };

    const fetchAssociateVehicles = useCallback(async () => {
        try {
            const response = await docsService.fetchAssociatedVehicle({
                publicationFormId: publicationFormId ? publicationFormId : '',
            });
            if (response.success && response.data && Object.keys(response.data).length > 0) {
                setAssociatedVehicles(response.data);
            }
        } catch (error) {
            logger.error(`Failed to fetch the associated vehicles for the publication [${publicationFormId}]`, {
                error,
                publicationFormId,
            });
        }
    }, [publicationFormId]);

    const isTechTips = isTechTipsLabel(publicationInfo.publicationType?.label);
    useEffect(() => {
        if (isTechTips) {
            setAssociatedVehicles(undefined);
        }
    }, [isTechTips]);

    const openVehiclePicker = useCallback(async () => {
        setSelectedSection('vehicles');
        if (hasLoadedVehicleOptionsRef.current) {
            return;
        }
        setIsLoadingVehicles(true);
        try {
            const response = await docsService.getAdminVehicleOptions();
            if (response.success) {
                setVehicleOptions(response.data);
                hasLoadedVehicleOptionsRef.current = true;
            }
        } catch (error) {
            logger.error('Failed to fetch the vehicles for the association picker', { error });
        } finally {
            setIsLoadingVehicles(false);
        }
    }, []);

    const saveAssociatedVehicles = useCallback(
        async (next: AssociatedVehiclesType) => {
            setAssociatedVehicles(next);
            if (!publicationFormId) {
                return;
            }
            try {
                await docsService.associateVehiclesToPublication({
                    publicationFormId,
                    body: {
                        vehicleIds: next.vehicles.map((vehicle) => vehicle.vehicleId),
                        allVehicles: next.allVehicles,
                    },
                });
                markSaved();
            } catch (error) {
                logger.error(`Failed to save the associated vehicles for [${publicationFormId}]`, {
                    error,
                    publicationFormId,
                });
            }
        },
        [markSaved, publicationFormId]
    );

    const fetchAssociatePublications = useCallback(async () => {
        try {
            const response = await docsService.fetchAssociatedPublications({
                publicationFormId: publicationFormId ? publicationFormId : '',
            });
            if (response.success && response.data && Object.keys(response.data).length > 0) {
                setAssociatedPublications(response.data.associatedDocs);
            }
        } catch (error) {
            logger.error(`Failed to fetch the associated publications for the publication [${publicationFormId}]`, {
                error,
                publicationFormId,
            });
        }
    }, [publicationFormId]);

    useEffect(() => {
        if (account && action == StisPublicationAction.New) {
            formActions.setCreatedAccount(
                account?.accountId,
                account?.name,
                account?.firstName,
                account?.lastName,
                account?.timeZone,
                account?.locale
            );
            formActions.setCreatedTime(getCurrentFormattedDate());
        }
    }, [account, action]);

    useEffect(() => {
        getFilters();
    }, []);

    useEffect(() => {
        if (publicationFormId && action !== StisPublicationAction.New) {
            fetchAssociateVehicles();
            fetchAssociatePublications();
        }
    }, [publicationFormId, action, fetchAssociateVehicles, fetchAssociatePublications]);

    const isLeavingDuringSend = leaveGuard.isLeaving && fileUploadStatus;
    useCloseLeaveWhenSent(fileUploadStatus, leaveGuard.isLeaving, leaveGuard.cancel);

    const leaveAndCancelSend = () => {
        fileSend.cancel();
        void leaveGuard.proceed();
    };

    const filePreview = getContentFilePreview(
        publicationInfo.contentFile,
        publicationInfo.publicationType?.label === 'Service Diagnostics'
    );

    const showFileError = useCallback(
        (error: OpenContentFileError) =>
            publicationDispatch(
                publicationsActions.setToastMessage({
                    toast: {
                        toastMessage:
                            error === 'blocked'
                                ? t(
                                      'stisPublications:documentInfo.fileActions.popupBlocked',
                                      'Your browser blocked the new tab. Allow pop-ups for this site to open the manual.'
                                  )
                                : t(
                                      'stisPublications:documentInfo.fileActions.openFailed',
                                      'The file could not be opened. Please try again.'
                                  ),
                        iconColor: 'white',
                        iconName: 'x-close',
                    },
                })
            ),
        [publicationDispatch, t]
    );

    const showArchiveError = useCallback(
        (data: unknown) =>
            publicationDispatch(
                publicationsActions.setToastMessage({
                    toast: {
                        toastMessage: getSaveErrorMessage(
                            data,
                            t('stisPublications:toast.archiveFailed', 'The published version was not archived')
                        ),
                        iconColor: 'white',
                        iconName: 'x-close',
                    },
                })
            ),
        [publicationDispatch, t]
    );

    const [blockedPublishCount, setBlockedPublishCount] = useState(0);
    const missingRequiredFields = useMemo(() => getMissingRequiredFields(publicationInfo), [publicationInfo]);
    const isMissingRequiredToPublish = missingRequiredFields.length > 0;
    const handleMissingRequired = useCallback(() => {
        checkRequiredFields();
        setBlockedPublishCount((count) => count + 1);
    }, [checkRequiredFields]);

    const showPublishBlocked = blockedPublishCount > 0 && isMissingRequiredToPublish;
    useEffect(() => {
        if (publicationInfo.status !== PublicationGroupStatus.Published && !showPublishBlocked) {
            setState({
                titleErrorMessage: '',
                publicationTypeErrorMessage: '',
                docCodeErrorMessage: '',
                descriptionErrorMessage: '',
                visibilityErrorMessage: '',
                contentLinkErrorMessage: '',
                contentFileErrorMessage: '',
            });
        }
    }, [publicationInfo.status, showPublishBlocked]);

    const publishGuard = usePublishGuard({
        documentId: publicationInfo.documentId,
        status: publicationInfo.status,
        versions: parentPublications,
        setVersions: setParentPublications,
        changeStatus: formActions.handleChangeStatusType,
        onArchiveFailed: showArchiveError,
        missingRequiredFields,
        onMissingRequired: handleMissingRequired,
    });

    const sidebarActions = useMemo(
        () => ({ ...formActions, handleChangeStatusType: publishGuard.requestStatusChange }),
        [formActions, publishGuard.requestStatusChange]
    );

    const uploadedFile = useOpenContentFile({
        publicationFormId,
        preview: filePreview,
        token,
        onError: showFileError,
    });

    const fileAction = useMemo(
        () =>
            filePreview
                ? {
                      label: t(
                          CONTENT_FILE_PREVIEW_LABELS[filePreview].key,
                          CONTENT_FILE_PREVIEW_LABELS[filePreview].defaultLabel
                      ),
                      onOpen: uploadedFile.open,
                  }
                : undefined,
        [filePreview, uploadedFile.open, t]
    );

    const leaveReason = leaveGuard.isLeaving ? getUnsavedChangesReason() : null;
    const hasFailedSave = autosaveStatus === 'error';
    // Draft is offered only where it would save; a zip on the wrong type, or no title, stops a draft too
    const canLeaveAsDraft =
        hasFailedSave ||
        leaveReason === BLOCKED_REASON.missingRequiredForPublished ||
        leaveReason === BLOCKED_REASON.fileNotReadyToPublish;

    const handleSaveDraftAndLeave = async () => {
        setIsSavingDraftToLeave(true);
        const result = await saveAsDraft();
        setIsSavingDraftToLeave(false);
        if (result.ok) {
            await leaveGuard.proceed();
            return;
        }
        leaveGuard.cancel();
        publicationDispatch(
            publicationsActions.setToastMessage({
                toast: {
                    toastMessage: result.message || 'The publication was not saved as a draft',
                    iconColor: 'white',
                    iconName: 'x-close',
                },
            })
        );
    };

    return (
        <>
            <PdfPreviewModal
                title={
                    publicationInfo.contentFile?.fileName ||
                    t(CONTENT_FILE_PREVIEW_LABELS.pdf.key, CONTENT_FILE_PREVIEW_LABELS.pdf.defaultLabel)
                }
                url={uploadedFile.pdfUrl}
                onHide={uploadedFile.closePdf}
                onDownload={() => uploadedFile.pdfUrl && downloadFromUrl(uploadedFile.pdfUrl)}
            />

            <PublicationModal
                show={isLeavingDuringSend}
                title={t('stisPublications:modal.leaveDuringUploadTitle', 'Your file is still uploading')}
                bodyHeader={t(
                    'stisPublications:modal.leaveDuringUploadBodyHeader',
                    'If you leave now, the upload stops and the file is lost'
                )}
                bodyContent={t(
                    'stisPublications:modal.leaveDuringUploadMessage',
                    'Stay until the upload reaches the processing step. After that you can leave and it keeps processing.'
                )}
                discardButton={t('stisPublications:buttons.leaveAndCancelUpload', 'Leave and cancel upload')}
                cancelButton={t('stisPublications:buttons.stay', 'Stay')}
                confirmButtonLoading={false}
                handleConfirmAction={leaveGuard.cancel}
                handleDiscardAction={leaveAndCancelSend}
                handleCancelAction={leaveGuard.cancel}
                onHideModal={leaveGuard.cancel}
                variant="primary"
            />

            <PublicationModal
                show={fileSend.showProcessingNotice && !leaveGuard.isLeaving}
                title={t('stisPublications:modal.processingTitle', 'Your file is processing')}
                bodyHeader={t('stisPublications:modal.processingBodyHeader', 'The upload is complete')}
                bodyContent={t(
                    'stisPublications:modal.processingMessage',
                    'Processing a large file can take a while. You can leave this publication now; it keeps processing and the file shows here when it is ready.'
                )}
                confirmButton={t('stisPublications:buttons.gotIt', 'Got it')}
                confirmButtonLoading={false}
                handleConfirmAction={fileSend.dismissProcessingNotice}
                handleCancelAction={fileSend.dismissProcessingNotice}
                onHideModal={fileSend.dismissProcessingNotice}
                variant="primary"
            />

            <PublicationModal
                show={leaveGuard.isLeaving && !fileUploadStatus}
                title={
                    canLeaveAsDraft
                        ? t('stisPublications:modal.leaveDraftTitle', 'Save changes as draft?')
                        : t('stisPublications:modal.leaveTitle', 'Leave without saving?')
                }
                bodyHeader={t('stisPublications:modal.leaveBodyHeader', 'Your changes are not saved')}
                bodyContent={
                    hasFailedSave
                        ? `${(leaveReason ?? SAVE_FAILED_REASON).replace(/\.\s*$/, '')}. ${t(
                              'stisPublications:modal.leaveSaveFailedMessage',
                              'Save it as a draft to keep your changes, or discard them.'
                          )}`
                        : leaveReason === BLOCKED_REASON.missingRequiredForPublished
                          ? t(
                                'stisPublications:modal.leaveMissingRequiredMessage',
                                'A published publication needs all required fields. Save it as a draft to keep your changes. It will be removed from search until it is published again.'
                            )
                          : leaveReason === BLOCKED_REASON.fileNotReadyToPublish
                            ? t(
                                  'stisPublications:modal.leaveFileNotReadyMessage',
                                  'It can be published once the file is ready. Save it as a draft to keep your changes.'
                              )
                            : `${leaveReason ?? ''}. ${t(
                                  'stisPublications:modal.leaveCannotSaveMessage',
                                  'Stay to fix it, or discard your changes.'
                              )}`
                }
                discardButton={t('stisPublications:modal.discardChanges', 'Discard changes')}
                cancelButton={t('stisPublications:buttons.stay', 'Stay')}
                confirmButton={
                    canLeaveAsDraft ? t('stisPublications:buttons.draftSave', 'Save as draft and exit') : undefined
                }
                confirmButtonLoading={isSavingDraftToLeave}
                handleConfirmAction={handleSaveDraftAndLeave}
                handleDiscardAction={() => void leaveGuard.proceed()}
                handleCancelAction={leaveGuard.cancel}
                onHideModal={leaveGuard.cancel}
                variant="primary"
            />

            <PublicationModal
                show={showDeletePublication}
                title={t('stisPublications:modal.deleteTitle', 'Delete publication?')}
                bodyHeader={t(
                    'stisPublications:modal.deleteBodyHeader',
                    'Are you sure you want to delete this publication?'
                )}
                bodyContent={t(
                    'stisPublications:modal.deleteBodyMessage',
                    'It will be removed permanently and cannot be undone.'
                )}
                cancelButton={t('stisPublications:buttons.cancel', 'Cancel')}
                confirmButton={t('stisPublications:buttons.delete', 'Delete')}
                confirmButtonLoading={false}
                handleCancelAction={() => setShowDeletePublication(false)}
                handleConfirmAction={() => handleDeletePublication(publicationFormId)}
                onHideModal={() => setShowDeletePublication(false)}
                variant="danger"
            />

            <PublicationModal
                show={Boolean(publishGuard.versionToArchive)}
                // No title: the single sentence below carries the whole message.
                title=""
                bodyContent={t(
                    'stisPublications:modal.archivePublishedBody',
                    'The status change will mark the previous version {{title}} (v{{version}}) status to Archive',
                    {
                        title: publishGuard.versionToArchive?.title,
                        version: publishGuard.versionToArchive?.version,
                    }
                )}
                cancelButton={t('stisPublications:buttons.cancel', 'Cancel')}
                confirmButton={t('stisPublications:buttons.confirm', 'Confirm')}
                confirmButtonLoading={publishGuard.isArchiving}
                handleCancelAction={publishGuard.cancel}
                handleConfirmAction={() => void publishGuard.archiveAndPublish()}
                onHideModal={publishGuard.cancel}
                variant="danger"
            />

            <AssociateVehiclesModal
                show={selectedSection === 'vehicles'}
                options={vehicleOptions}
                isLoading={isLoadingVehicles}
                selected={(associatedVehicles?.vehicles ?? []).map((vehicle) => vehicle.vehicleId)}
                allVehicles={associatedVehicles?.allVehicles ?? false}
                onHide={() => setSelectedSection('')}
                onSave={async ({ vehicleIds, allVehicles }) => {
                    const picked = new Set(vehicleIds);
                    await saveAssociatedVehicles({
                        vehicles: vehicleOptions.filter((vehicle) => picked.has(vehicle.vehicleId)),
                        allVehicles,
                    });
                    setSelectedSection('');
                }}
            />

            <AssociateSectionModal
                show={selectedSection == 'publications'}
                onHide={() => setSelectedSection('')}
                publicationFormId={publicationFormId || ''}
                setAssociatePublications={handleAssociatedPublicationsSaved}
                associatePublications={associatedPublications}
                selectedSection={selectedSection}
                setPublicationInfo={setPublicationInfo}
            />

            <DetailView>
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        <StisPublicationFormHeader
                            publicationFormId={publicationFormId}
                            publicationStatus={publicationStatus}
                            statusOfPublication={statusOfPublication}
                            setShowDeletePublication={setShowDeletePublication}
                            publicationInfo={publicationInfo}
                            handleDuplicatePublication={handleDuplicatePublication}
                            fileUploadStatus={fileUploadStatus}
                            autosaveIndicator={
                                <AutosaveIndicator
                                    key={blockedPublishCount}
                                    status={autosave.status}
                                    savedAt={autosave.savedAt}
                                    errorMessage={autosave.errorMessage}
                                    blockedReason={
                                        showPublishBlocked
                                            ? BLOCKED_REASON.missingRequiredForPublished
                                            : autosave.blockedReason
                                    }
                                    fileNote={
                                        fileUploadStatus
                                            ? 'File still uploading'
                                            : isFileProcessing
                                              ? 'File still processing'
                                              : null
                                    }
                                    onRetry={autosave.retry}
                                />
                            }
                        />

                        <DetailViewContent>
                            <DetailViewBody className="pt-5 pb-3 pr-0 overflow-hidden">
                                <Typography
                                    style={{
                                        fontFamily: 'Roboto',
                                        fontWeight: '600',
                                        fontSize: 21,
                                        lineHeight: 32,
                                        letterSpacing: 0,
                                    }}
                                >
                                    {t('stisPublications:documentInfo.headers.section1', 'Document Information')}
                                </Typography>

                                <div
                                    style={{ height: '95%', overflowY: 'auto' }}
                                    className={`pr-3 ${classes.scrollBar}`}
                                >
                                    <DocumentInfo
                                        publicationInfo={publicationInfo}
                                        actions={formActions}
                                        publicationTypes={publicationTypes}
                                        fileUploadStatus={fileUploadStatus}
                                        setFileUploadStatus={setFileUploadStatus}
                                        state={state}
                                        action={action}
                                        onFileSent={handleFileSent}
                                        onSendStart={fileSend.start}
                                        onSendProgress={fileSend.setProgress}
                                        sendProgress={fileSend.progress}
                                        fileAction={fileAction}
                                        uploadPhase={upload.phase}
                                        lastCheckedAt={upload.lastCheckedAt}
                                        onCheckAgain={upload.checkAgain}
                                    />

                                    {!isTechTips && (
                                        <AssociateSections
                                            headerText={t(
                                                'stisPublications:documentInfo.headers.section2',
                                                'Associate Vehicles'
                                            )}
                                            buttonText={t(
                                                'stisPublications:documentInfo.buttonText.associateVehicles',
                                                'Associate Vehicles'
                                            )}
                                            styleProps={{ marginTop: 12 }}
                                            onClick={openVehiclePicker}
                                            AssociatedVehicles={
                                                associatedVehicles?.allVehicles ||
                                                (associatedVehicles?.vehicles ?? []).length > 0 ? (
                                                    <AssociatedVehiclesSummary
                                                        allVehicles={associatedVehicles?.allVehicles ?? false}
                                                        vehicles={associatedVehicles?.vehicles ?? []}
                                                        onEdit={openVehiclePicker}
                                                    />
                                                ) : null
                                            }
                                        />
                                    )}

                                    <AssociateSections
                                        headerText={t(
                                            'stisPublications:documentInfo.headers.section3',
                                            'Associate Related Publications'
                                        )}
                                        buttonText={t(
                                            'stisPublications:documentInfo.buttonText.associatePublications',
                                            'Associate Publications'
                                        )}
                                        styleProps={{ borderBottom: `1px solid ${colors.grayThree}` }}
                                        onClick={() => setSelectedSection('publications')}
                                        AssociatedPublications={
                                            associatedPublications && associatedPublications.length > 0 ? (
                                                <AssociatePublicationsCard
                                                    associatedDocs={associatedPublications}
                                                    setAssociatedPublications={handleAssociatedPublicationsSaved}
                                                    filterResponse={filterResponse}
                                                    publicationInfo={publicationInfo}
                                                    setPublicationInfo={setPublicationInfo}
                                                />
                                            ) : null
                                        }
                                    />

                                    <DocumentSearchMetaData
                                        publicationInfo={publicationInfo}
                                        actions={formActions}
                                        publicationFormId={publicationFormId}
                                    />

                                    <PreviewDescription
                                        publicationInfo={publicationInfo}
                                        actions={formActions}
                                        state={state}
                                    />
                                </div>
                            </DetailViewBody>

                            <PublicationFormRightSidebar
                                checkRequiredFields={checkRequiredFields}
                                publicationInfo={publicationInfo}
                                actions={sidebarActions}
                                statusOfPublication={statusOfPublication}
                                creatorUser={creatorUser}
                                updatedUser={updatedUser}
                                parentPublications={parentPublications}
                                publishBlockedMessage={publishBlockedMessage}
                            />
                        </DetailViewContent>
                    </>
                )}
            </DetailView>
        </>
    );
};

export default StisPublicationForm;
